<template>
  <widget-wrapper
    title="Alarmas"
    widget-id="widget-alerts"
    widget-name="Alarmas"
    :has-help="true"
    :help-url="url_iframe"
    :help-title="help_title"
    :has-alternate-action="true"
    alternate-action-icon="launch"
    alternate-action-url="/app/graficador-dinamico"
    theme="dark"
    @close-widget="_closeWidget"
    @show-help="_sendDataToIframe"
  >
    <div v-if="loading" class="widget-loading">
      <div class="spinner"></div>
    </div>
    
    <div v-else class="alerts-content">
      <!-- Status Toggle -->
      <div class="status-toggle">
        <div 
          class="status-btn" 
          :class="{ 'active': showStatus === 'pending' }"
          @click="showStatus = 'pending'"
        >
          Pendientes ({{ pending_alerts.length }})
        </div>
        <div 
          class="status-btn" 
          :class="{ 'active': showStatus === 'executed' }"
          @click="showStatus = 'executed'"
        >
          Ejecutadas ({{ executed_alerts.length }})
        </div>
      </div>

      <!-- Category Tabs -->
      <div class="category-tabs" v-if="available_categories.length > 0">
        <div 
          class="category-tab" 
          :class="{ 'active': activeCategory === category.key }"
          @click="activeCategory = category.key"
          v-for="category in available_categories" 
          :key="category.key"
        >
          {{ category.name }} ({{ getCategoryAlerts(category.key).length }})
        </div>
      </div>
      
      <!-- Alerts List -->
      <div class="alerts-list">
        <div v-if="currentCategoryAlerts.length > 0">
          <div 
            v-for="(alert, index) in currentCategoryAlerts" 
            :key="alert.id" 
            class="alert-item"
            :class="{ 'executed': alert.status == '1' }"
          >
            <div class="alert-number">{{ index + 1 }}</div>
            <div class="alert-item-content">
              <div class="alert-ticker">{{ alert.ticker_name }}</div>
              <div class="alert-details">
                {{ alert.alert_field_string }} {{ alert.alert_mode_string }} {{ alert.alert_data }}
                <div v-if="alert.status == '1'" class="alert-executed-date">
                  {{ alert.executed_date || alert.modified }}
                </div>
              </div>
            </div>
            <button 
              v-if="alert.status == '0'" 
              @click="_confirmDeleteAlarm(alert)" 
              class="delete-btn"
              :title="'Eliminar alarma de ' + alert.ticker_name"
            >
              <span class="material-icons">delete</span>
            </button>
          </div>
        </div>
        <div v-else class="empty-alerts">
          <p v-if="showStatus === 'pending'">No hay alarmas pendientes en esta categoría</p>
          <p v-else>No hay alarmas ejecutadas en esta categoría</p>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirmation" class="modal-overlay" @click="cancelDelete">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Confirmar eliminación</h3>
        </div>
        <div class="modal-body">
          <p>¿Estás seguro de que deseas eliminar esta alarma?</p>
          <div class="alert-preview" v-if="alertToDelete">
            <strong>{{ alertToDelete.ticker_name }}</strong><br>
            {{ alertToDelete.alert_field_string }} {{ alertToDelete.alert_mode_string }} {{ alertToDelete.alert_data }}
          </div>
        </div>
        <div class="modal-footer">
          <button @click="cancelDelete" class="btn-cancel">Cancelar</button>
          <button @click="confirmDelete" class="btn-confirm">Eliminar</button>
        </div>
      </div>
    </div>
  </widget-wrapper>
</template>

<script>
import { mapGetters } from "vuex";
import APICaller from "@/components/codeComponents/APICaller.js";
import WidgetWrapper from "@/components/widgets/WidgetWrapper.vue";

export default {
  mixins: [APICaller],
  name: "Alarmas",
  components: {
    WidgetWrapper
  },
  data() {
    return {
      widget_name: { id: "widget-alerts", name: "Alarmas" },
      loading: true,
      alerts: [],
      showStatus: "pending", // pending or executed
      activeCategory: "all",
      uid: "",
      url_iframe: "https://player.vimeo.com/video/305716774?title=0&byline=0&portrait=0&autoplay=1",
      help_title: "Ayuda sobre alertas",
      categories: {
        'all': 'Todas',
        'current_value': 'Valor Actual',
        'entry_value': 'Entrada',
        'entry_distance': 'Distancia Entrada',
        'support_value': 'Soporte',
        'stop_value': 'Stop'
      },
      showDeleteConfirmation: false,
      alertToDelete: null
    };
  },
  mounted() {
    this._loadData();
  },
  computed: {
    ...mapGetters(["is_mobile_device", "_g_User"]),
    
    pending_alerts() {
      return this.alerts.filter(alert => alert.status == "0");
    },
    
    executed_alerts() {
      return this.alerts.filter(alert => alert.status == "1");
    },

    current_alerts() {
      return this.showStatus === 'pending' ? this.pending_alerts : this.executed_alerts;
    },

    available_categories() {
      const categoriesWithAlerts = [];
      
      // Always show "Todas" if there are any alerts
      if (this.current_alerts.length > 0) {
        categoriesWithAlerts.push({ key: 'all', name: this.categories.all });
      }

      // Check which categories have alerts
      Object.keys(this.categories).forEach(key => {
        if (key !== 'all' && this.getCategoryAlerts(key).length > 0) {
          categoriesWithAlerts.push({ key, name: this.categories[key] });
        }
      });

      return categoriesWithAlerts;
    },

    currentCategoryAlerts() {
      return this.getCategoryAlerts(this.activeCategory);
    }
  },
  watch: {
    available_categories: {
      handler(newCategories) {
        // If current active category is not available, switch to first available
        if (newCategories.length > 0 && !newCategories.some(cat => cat.key === this.activeCategory)) {
          this.activeCategory = newCategories[0].key;
        }
      },
      immediate: true
    },
    showStatus() {
      // Reset to "all" category when changing status
      this.activeCategory = 'all';
    }
  },
  methods: {
    _loadData() {
      this.loading = true;
      if (this._g_User && this._g_User.uid) {
        this.uid = this._g_User.uid;
        this._getAlarms();
      } else {
        this.loading = false;
      }
    },

    getCategoryAlerts(categoryKey) {
      if (categoryKey === 'all') {
        return this.current_alerts;
      }

      return this.current_alerts.filter(alert => {
        const fieldString = alert.alert_field_string;
        switch (categoryKey) {
          case 'current_value':
            return fieldString === 'Valor actual';
          case 'entry_value':
            return fieldString === 'Valor de entrada';
          case 'entry_distance':
            return fieldString === 'Distancia de entrada';
          case 'support_value':
            return fieldString === 'Valor de soporte';
          case 'stop_value':
            return fieldString === 'Valor del STOP';
          default:
            return false;
        }
      });
    },

    _confirmDeleteAlarm(alert) {
      this.alertToDelete = alert;
      this.showDeleteConfirmation = true;
    },

    cancelDelete() {
      this.showDeleteConfirmation = false;
      this.alertToDelete = null;
    },

    confirmDelete() {
      if (this.alertToDelete) {
        this._deleteAlarm(this.alertToDelete.id);
        this.showDeleteConfirmation = false;
        this.alertToDelete = null;
      }
    },
    
    _sendDataToIframe: function() {
      this.$emit("url_iframe", this.url_iframe);
      this.$emit("help_title", this.help_title);
      this.$emit("load_iframe");
    },

    _deleteAlarm: function(id) {
      this.loading = true;
      
      let params = new URLSearchParams();
      params.append("uid", this.uid);
      params.append("alert_id", id);

      let success = () => {
        this._getAlarms();
      };

      let failure = () => {
        this.loading = false;
      };

      let url = "/api/v1/eurekers-alerts/delete";

      const successHandler = new APICaller.SuccessHandler(success);
      const failureHandler = new APICaller.FailureHandler(failure, "309", "Error borrando alarma.");

      this._postAPICall(url, params, successHandler, failureHandler);
    },

    _getAlarms: function() {
      let params = new URLSearchParams();
      params.append("uid", this.uid);

      let success = (response) => {
        this.alerts = response.data || [];
        this.loading = false;
      };

      let failure = () => {
        this.alerts = [];
        this.loading = false;
      };

      let url = "/api/v1/eurekers-alerts/get-user-alerts";

      const successHandler = new APICaller.SuccessHandler(success);
      const failureHandler = new APICaller.FailureHandler(failure, "310", "Error recuperando alarmas.");

      this._postAPICall(url, params, successHandler, failureHandler);
    },

    _closeWidget: function() {
      this.$emit("widget_name", this.widget_name);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/components/widgets/WidgetStyles.scss";

.widget-loading {
  @extend .widget-loading-base;
  background-color: var(--color-background, #0F0F1A);
}

.spinner {
  @extend .loading-spinner;
}

.alerts-content {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.status-toggle {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 4px;
  
  .status-btn {
    flex: 1;
    text-align: center;
    padding: 8px 4px;
    font-size: 12px;
    font-weight: 600;
    color: var(--color-text-secondary, #BFC2CD);
    cursor: pointer;
    transition: all 0.2s ease;
    
    &.active {
      color: var(--primary, #00AEDD);
      border-bottom: 2px solid var(--primary, #00AEDD);
    }
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.05);
    }
  }
}

.category-tabs {
  display: flex;
  overflow-x: auto;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  
  .category-tab {
    white-space: nowrap;
    padding: 6px 8px;
    font-size: 10px;
    color: var(--color-text-secondary, #BFC2CD);
    cursor: pointer;
    transition: all 0.2s ease;
    border-bottom: 2px solid transparent;
    
    &.active {
      color: var(--color-text-primary, #FFFFFF);
      border-bottom-color: rgba(255, 255, 255, 0.3);
    }
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.05);
    }
  }
}

.alerts-list {
  flex: 1;
  padding: 8px;
  overflow-y: auto;
}

.alert-item {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  margin-bottom: 6px;
  border-radius: 4px;
  background-color: var(--color-background);
  position: relative;
  border-left: 3px solid var(--primary, #00AEDD);
  
  &.executed {
    border-left-color: var(--color-success, #57A869);
  }
}

.alert-number {
  background-color: var(--primary, #00AEDD);
  color: #FFFFFF;
  font-size: 10px;
  font-weight: 600;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  flex-shrink: 0;

  .executed & {
    background-color: var(--color-success, #57A869);
  }
}

.alert-item-content {
  flex-grow: 1;
  overflow: hidden;
}

.alert-ticker {
  font-weight: 600;
  font-size: 12px;
  color: var(--color-text-primary, #FFFFFF);
  margin-bottom: 2px;
}

.alert-details {
  font-size: 11px;
  color: var(--color-text-secondary, #BFC2CD);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.alert-executed-date {
  font-size: 10px;
  color: var(--color-success, #57A869);
  margin-top: 2px;
}

.delete-btn {
  background: none;
  border: none;
  color: var(--color-text-secondary, #BFC2CD);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  padding: 0;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    background-color: rgba(255, 69, 69, 0.2);
    color: #FF4545;
  }
  
  .material-icons {
    font-size: 16px;
  }
}

.empty-alerts {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 10px;
  
  p {
    color: var(--color-text-secondary, #BFC2CD);
    font-size: 12px;
    font-style: italic;
    text-align: center;
  }
}

// Delete Confirmation Modal
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background-color: var(--color-background, #1A1A2E);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  min-width: 300px;
  max-width: 400px;
  margin: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.modal-header {
  padding: 16px 20px 0;
  
  h3 {
    color: var(--color-text-primary, #FFFFFF);
    font-size: 16px;
    font-weight: 600;
    margin: 0;
  }
}

.modal-body {
  padding: 16px 20px;
  
  p {
    color: var(--color-text-secondary, #BFC2CD);
    font-size: 14px;
    margin: 0 0 12px 0;
  }
}

.alert-preview {
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 12px;
  color: var(--color-text-secondary, #BFC2CD);
  
  strong {
    color: var(--color-text-primary, #FFFFFF);
  }
}

.modal-footer {
  padding: 12px 20px 20px;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.btn-cancel,
.btn-confirm {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancel {
  background-color: rgba(255, 255, 255, 0.1);
  color: var(--color-text-secondary, #BFC2CD);
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
    color: var(--color-text-primary, #FFFFFF);
  }
}

.btn-confirm {
  background-color: #FF4545;
  color: #FFFFFF;
  
  &:hover {
    background-color: #FF3333;
  }
}
</style>
